/**
 * component-bundle-quantity.js — Block A (_bundle-quantity) interactions.
 *
 * Loaded (defer, after component-bundle-core.js) by the parent block; the
 * window guard dedupes across instances (Rule J12). Everything is
 * document-level delegated (J1/J2) keyed off closest('[data-bundle-qty]') so
 * listeners survive SRA root swaps with no re-bind.
 *
 * Owns the radiogroup mechanics (plan amendment 4): click / Space / Enter
 * selection, roving tabindex, Arrow keys (move focus AND select — WAI-ARIA
 * radio pattern), the CTA add flow, and the list vertical cap. Core
 * (window.theme.bundle) keeps the state map + variant-changed restore and
 * does all CTA/savings/unit stamping via theme.bundle.restamp().
 *
 * U2 additions (S23-P2-PLAN §2.3/§4): the columns/tabs overflow engine —
 * syncArrows (real-overflow detection unhides the arrows when
 * data-arrows="auto") + syncEnds (:disabled at the scroll ends, via ONE
 * capture-phase document scroll listener — scroll doesn't bubble) + arrow
 * clicks (delegated, scrollBy 0.8*clientWidth) — and the tabs panel measure:
 * measurePanel sets data-bq-ready (CSS panel-display gate) then exposes the
 * selected panel's offsetHeight as --bq-panel-h on the root. Re-eval sites:
 * init, selection, SRA re-run (all via refresh), plus ONE debounced window
 * resize (which deliberately does NOT re-run the list vcap — plan C9: list
 * JS behavior stays U1-identical). All guards early-return on list roots
 * before any DOM/style write (§7 regression contract).
 *
 * U3 hooks (S23-P3-PLAN §5): a bundle:popup-opened listener re-runs refresh
 * on the moved pop-body (everything measured 0 while hidden), and the CTA
 * add flow closes the popup before the after-add matrix when the root lives
 * inside a dialog. Popup mechanics themselves live in core.
 */

(function () {
  'use strict';

  if (window.__kvBundleQty) return;
  window.__kvBundleQty = true;

  function core() {
    return window.theme && window.theme.bundle;
  }

  function tiers(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-bqt]'));
  }

  function select(root, tier, focus) {
    var list = tiers(root);
    list.forEach(function (t) {
      var on = t === tier;
      t.classList.toggle('is-selected', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    if (focus) tier.focus();
    var engine = core();
    if (engine) {
      var blockId = root.getAttribute('data-block-id');
      engine.state[blockId] = engine.state[blockId] || {};
      engine.state[blockId].tier = list.indexOf(tier);
      engine.restamp(root);
    }
    measurePanel(root); // tabs-only (internal guard); panel height tracks the selected tier
  }

  function handleCta(button) {
    var root = button.closest('[data-bundle-qty]');
    var engine = core();
    if (!root || !engine) return;
    var tier = root.querySelector('[data-bqt].is-selected') || root.querySelector('[data-bqt]');
    if (!tier) return;
    var error = root.querySelector('[data-bq-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    var variantId = engine.selectedVariantId(root);
    if (!variantId) return;
    button.disabled = true;
    engine
      .addItems([{ id: variantId, quantity: parseInt(tier.getAttribute('data-qty'), 10) || 1 }])
      .then(function () {
        button.disabled = false;
        // Popup body: close (restore/discard) BEFORE the after-add matrix so
        // the drawer never stacks under the dialog (spec §5). Inline roots
        // never match closest('dialog.overlay') — flow unchanged.
        if (root.closest('dialog.overlay')) engine.closeBundleOverlays();
        engine.afterAdd(tier.getAttribute('data-code'), root.getAttribute('data-after-add'), root);
      })
      .catch(function (err) {
        button.disabled = false;
        if (error) {
          error.textContent = (err && err.message) || 'Error';
          error.hidden = false;
        }
      });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var arrow = e.target.closest('.bundle-qty__arrow');
    if (arrow) {
      var arrowRoot = arrow.closest('[data-bundle-qty]');
      var sc = arrowRoot && getScroller(arrowRoot);
      if (sc) {
        // LTR store: scrollLeft semantics; RTL would need normalization (spec §5).
        var dir = arrow.classList.contains('bundle-qty__arrow--next') ? 1 : -1;
        sc.scrollBy({ left: dir * 0.8 * sc.clientWidth, behavior: 'smooth' });
      }
      return;
    }
    var cta = e.target.closest('[data-bq-cta]');
    if (cta) {
      handleCta(cta);
      return;
    }
    var tier = e.target.closest('[data-bqt]');
    if (!tier) return;
    var root = tier.closest('[data-bundle-qty]');
    if (root) select(root, tier, false);
  });

  document.addEventListener('keydown', function (e) {
    if (!e.target.closest) return;
    var tier = e.target.closest('[data-bqt]');
    if (!tier) return;
    var root = tier.closest('[data-bundle-qty]');
    if (!root) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      select(root, tier, false);
      return;
    }
    var list = tiers(root);
    var index = list.indexOf(tier);
    var next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      next = list[(index + 1) % list.length];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      next = list[(index - 1 + list.length) % list.length];
    }
    if (next) {
      e.preventDefault();
      select(root, next, true);
    }
  });

  /**
   * List vertical cap: with more tiers than the visible count, cap the items
   * box at the Nth row's bottom edge (rows[N-1] offsetTop + offsetHeight,
   * relative to row[0] — exactly N rows, no trailing gap) and scroll the
   * rest (no-JS: uncapped). Visible counts are read from the root's inline
   * --bq-visible-d / --bq-visible-m custom properties (0/absent -m = desktop
   * value); the media match is read at run time, re-applied on SRA swaps.
   */
  function applyVcap(root) {
    if (!root.classList.contains('bundle-qty--list')) return;
    var items = root.querySelector('.bundle-qty__items');
    if (!items) return;
    var rows = tiers(root);
    var styles = getComputedStyle(root);
    var visible = parseInt(styles.getPropertyValue('--bq-visible-d'), 10) || 3;
    if (window.matchMedia('(max-width: 749px)').matches) {
      var mobile = parseInt(styles.getPropertyValue('--bq-visible-m'), 10);
      if (mobile > 0) visible = mobile;
    }
    if (rows.length <= visible) {
      items.style.maxBlockSize = '';
      items.style.overflowY = '';
      return;
    }
    items.style.maxBlockSize = (rows[visible - 1].offsetTop + rows[visible - 1].offsetHeight - rows[0].offsetTop) + 'px';
    items.style.overflowY = 'auto';
  }

  function isHorizontal(root) {
    return root.classList.contains('bundle-qty--columns') || root.classList.contains('bundle-qty--tabs');
  }

  function getScroller(root) {
    return root.querySelector('.bundle-qty__scroller');
  }

  /**
   * Tabs panel measure (spec §4.2): set data-bq-ready FIRST (the CSS gate
   * that displays the selected panel — plan C4), then expose the panel's
   * offsetHeight as --bq-panel-h on the root so __items reserves the space.
   * Tabs roots only — list/columns roots are never touched (§7).
   */
  function measurePanel(root) {
    if (!root.classList.contains('bundle-qty--tabs')) return;
    root.setAttribute('data-bq-ready', '');
    var panel = root.querySelector('[data-bqt].is-selected .bundle-qty__panel') ||
      root.querySelector('[data-bqt] .bundle-qty__panel');
    if (panel) root.style.setProperty('--bq-panel-h', panel.offsetHeight + 'px');
  }

  /** Disabled state at the scroll ends (§5). */
  function syncEnds(root) {
    var sc = getScroller(root);
    var prev = root.querySelector('.bundle-qty__arrow--prev');
    var next = root.querySelector('.bundle-qty__arrow--next');
    if (!sc || !prev || !next) return;
    prev.disabled = sc.scrollLeft <= 1;
    next.disabled = sc.scrollLeft >= sc.scrollWidth - sc.clientWidth - 1;
  }

  /** Real-overflow detection -> wake/park the arrows (data-arrows="auto" only). */
  function syncArrows(root) {
    if (!isHorizontal(root)) return;
    var sc = getScroller(root);
    var prev = root.querySelector('.bundle-qty__arrow--prev');
    var next = root.querySelector('.bundle-qty__arrow--next');
    if (!sc || !prev || !next) return;
    var show = root.getAttribute('data-arrows') === 'auto' && sc.scrollWidth > sc.clientWidth + 1;
    [prev, next].forEach(function (b) {
      b.hidden = !show;
      if (show) {
        b.removeAttribute('aria-hidden');
        b.removeAttribute('tabindex');
      } else {
        b.setAttribute('aria-hidden', 'true');
        b.setAttribute('tabindex', '-1');
      }
    });
    if (show) syncEnds(root);
  }

  /* F2 §3/§4: stamp the trigger subtitle (popup shell) + the header max badge
     (root) from the greatest tier data-discount — the JS surface S20 prescribes
     (tier roots emit data-discount; the parent's settings read of children is
     nil-only, confirmed live at U8). ONE read of server-rendered attrs; self-
     degrades to a no-op when no tier carries a discount (a blank/zero config
     leaves the templates hidden, never an error). The badge lives INSIDE the
     root (both modes); the subtitle lives on [data-bundle-pop-shell] (popup mode
     only) and is reachable while the root is still "home" — init / section:load /
     SRA, before the open moves the root into the dialog. No observers, no new
     listeners. */
  function stampOffers(root) {
    var max = 0;
    tiers(root).forEach(function (t) {
      var d = parseInt(t.getAttribute('data-discount'), 10) || 0;
      if (d > max) max = d;
    });
    if (max <= 0) return;
    function stamp(el) {
      var tpl = el.getAttribute('data-bq-badge-template');
      if (tpl == null) tpl = el.getAttribute('data-bq-sub-template');
      if (tpl == null) return;
      el.textContent = tpl.replace('{pct}', max);
      el.hidden = false;
      var sep = el.previousElementSibling;
      if (sep && sep.classList.contains('bundle-qty__trigger-sep')) sep.hidden = false;
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-bq-badge-template]'), stamp);
    var shell = root.closest('[data-bundle-pop-shell]');
    if (shell) Array.prototype.forEach.call(shell.querySelectorAll('[data-bq-sub-template]'), stamp);
  }

  function refresh(root) {
    applyVcap(root); // list-only (internal guard)
    syncArrows(root); // columns/tabs-only
    measurePanel(root); // tabs-only
    stampOffers(root); // F2 §3/§4 — subtitle + max-badge JS-stamp (idempotent, self-degrading)
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-bundle-qty]').forEach(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('product:variant-changed', function (e) {
    var root = e.detail && e.detail.root;
    if (root) initAll(root);
  });

  /* Theme-editor re-init (S23-FIX1 F1): any section/block setting change
     re-renders the section and arrives as shopify:section:load (block:select
     never implies a re-render — its detail.load flag exists for exactly that
     distinction). Editor-only event; always registered, zero storefront cost. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  /* Refresh-on-open (U3): the pop-body was measured while hidden (vcap /
     arrows / panel all 0) — core dispatches bundle:popup-opened right after
     the synchronous overlay open (no overlay event exists on the moveId
     path); re-run the measurements on the now-visible body. */
  document.addEventListener('bundle:popup-opened', function (e) {
    if (e.detail && e.detail.root) refresh(e.detail.root);
  });

  /* Ends-state on scroll: capture-phase document listener (scroll doesn't
     bubble; capture = delegation, survives SRA swaps), rAF-throttled. */
  var endsTick = false;
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('bundle-qty__scroller')) return;
    if (endsTick) return;
    endsTick = true;
    requestAnimationFrame(function () {
      endsTick = false;
      var root = t.closest('[data-bundle-qty]');
      if (root) syncEnds(root);
    });
  }, true);

  /* Single debounced resize (no ResizeObserver — §4.2 budget). Deliberately
     skips applyVcap (plan C9 — list behavior stays U1-identical). */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-bundle-qty]').forEach(function (root) {
        syncArrows(root);
        measurePanel(root);
      });
    }, 150);
  });
})();
